import { ErrorMessage, Field, FieldArray, Formik, FormikProvider, useFormik } from "formik";
import { debounce, initial, set } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { Button, Card, CardBody, CardHeader, Col, Container, Form, FormFeedback, FormGroup, Input, InputGroup, InputGroupText, Label, Modal, ModalBody, ModalFooter, ModalHeader, Pagination, PaginationItem, PaginationLink, Row, Spinner, Table, Toast, ToastBody, ToastHeader } from "reactstrap";
import './CommandeLine.css';
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleLeft, FaAngleRight, FaBarcode, FaBox, FaBoxes, FaCog, FaEdit, FaImage, FaList, FaMoneyBillWave, FaPlus, FaSave, FaSearch, FaTag, FaTh, FaTimes, FaTrash, FaWarehouse } from "react-icons/fa";
import { t } from "i18next";
import { useDispatch, useSelector } from "react-redux";
import { supplierActions } from "../../sagas/supplierSlice";
import Swal from "sweetalert2";
import { productActions } from "../../sagas/productSlice";
import config from "../../helpers/config";
import { formatPrice, getPrixAchat, preventScroll, roundToNearestInteger } from "../../helpers/utility";
import Select from 'react-select';
import * as Yup from 'yup';
import { useNavigate } from "react-router-dom";
import { categoryActions } from "../../sagas/categorySlice";


const CommandeLine = ({ formik, disabled }) => {
    const { values, setFieldValue, errors, touched } = formik;
    const [items, setItems] = useState([]);
    const [productModal, setProductModal] = useState(false);
    const toggleProductModal = () => setProductModal(!productModal);
    const [currentPage, setCurrentPage] = useState(1);
    const navigate = useNavigate();
    const [chooseProductModal, setChooseProductModal] = useState(false);
    const toggleChooseProductModal = () => setChooseProductModal(!chooseProductModal);
    const [modalSearchTerm, setModalSearchTerm] = useState('');
    const [displayMode, setDisplayMode] = useState('table');
    const { list } = useSelector(state => state.categories); // Assuming you have a Redux store with categories

    const TVA_OPTIONS = [
        { code: 0.2, libelle: '20%' },
        { code: 0.1, libelle: '10%' },
        { code: 0.01, libelle: '7%' },
        { code: 0.14, libelle: '14%' }
    ];

    const unites = useSelector(status => status.products.unites); // Assuming you have a Redux store with unite

    const {
        products,
        loading,
        totalPages,
        totalElements,
        pageNumber,
        pageSize,
        error,
    } = useSelector((status) => status.products);


    const dispatch = useDispatch();
    const [isSearching, setIsSearching] = useState(false);


    useEffect(() => {
        dispatch(productActions.findAllUnites());
        setItems(values.lignes.map(l => {
            // Find product by articleId
            const product = products?.find(p => p.id === l.articleId);
            let prices = l.prices;
            if (!prices && product) {
                prices = [product.prixVenteTTC1, product.prixVenteTTC2, product.prixVenteTTC3].filter(Boolean);
            } else if (!prices) {
                prices = [l.prixUnitaireTTC].filter(Boolean);
            }
            const uniteInfo = unites?.find(u => u.code === l.unite);
            return {
                ...l,
                unite: uniteInfo?.libelle || l.unite || '',
                prices,
            };
        }));
        dispatch(categoryActions.list({ query: '' })); // Fetch categories if needed

    }, [values.lignes]);

    useEffect(() => {
        dispatch(productActions.findAll({
            search: modalSearchTerm,
            page: currentPage - 1,
            size: 20,
        }));

    }, [currentPage, chooseProductModal]);


    const recalculateAndSetItems = (newItems) => {
        const toNum = v => {
            const n = parseFloat(v);
            return Number.isFinite(n) ? n : 0;
        };
        const toCents = v => Math.round(v * 100);
        const fromCents = c => roundToNearestInteger(c / 100);

        const normalizedItems = newItems.map((item) => {
            const quantity = toNum(item.quantiteLivree);
            const unitTTC = toNum(item.prixUnitaireTTC);
            const tauxTVA = toNum(item.tauxTVA);
            const unitHT = tauxTVA > -1 ? (unitTTC / (1 + tauxTVA)) : unitTTC;

            const lineTTCc = toCents(quantity * unitTTC);
            const lineHTc = toCents(quantity * unitHT);

            return {
                ...item,
                prixUnitaireHT: fromCents(toCents(unitHT)),
                montantHT: fromCents(lineHTc),
                montantTTC: fromCents(lineTTCc),
            };
        });

        let htCumulC = 0;
        let ttcCumulC = 0;
        let taxeTotalC = 0;

        normalizedItems.forEach(item => {
            const q = toNum(item.quantiteLivree);
            const uTTC = toNum(item.prixUnitaireTTC);
            const tva = toNum(item.tauxTVA);
            const uHT = tva > -1 ? (uTTC / (1 + tva)) : uTTC;

            const ttcC = toCents(q * uTTC);
            const htC = toCents(q * uHT);
            const tvaC = ttcC - htC;

            htCumulC += htC;
            ttcCumulC += ttcC;
            taxeTotalC += tvaC;
        });

        setItems(normalizedItems);
        setFieldValue('lignes', normalizedItems);
        setFieldValue('montantTotalHT', fromCents(htCumulC));
        setFieldValue('montantTotalTTC', fromCents(ttcCumulC));
        setFieldValue('montantTotalTaxe', fromCents(taxeTotalC));
    };

    const handleInputChange = (index, event) => {

        const newItems = [...items];
        const updatedItem = { ...newItems[index], [event.target.name]: event.target.value };
        newItems[index] = updatedItem;
        recalculateAndSetItems(newItems);
    };


    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };



    const handleDeleteItem = (index) => {
        // Small helpers (keep in sync with handleInputChange)
        const toNum = v => {
            const n = parseFloat(v);
            return Number.isFinite(n) ? n : 0;
        };
        const toCents = v => Math.round(v * 100);
        const fromCents = c => roundToNearestInteger(c / 100);

        // Remove the line
        const newItems = [...items];
        newItems.splice(index, 1);

        recalculateAndSetItems(newItems);
    };


    const debouncedModalSearch = useCallback(
        debounce((query) => {
            setIsSearching(true);
            dispatch(productActions.findAll({
                search: query,
                page: 0,
                size: pageSize,
                onSuccess: () => setIsSearching(false)
            }));
            setIsSearching(false);
        }, 1000), // Wait 1 second after user stops typing
        [modalSearchTerm]
    );

    const handleProductSelect = async (product) => {
        if (!values.clientId) {
            Swal.fire({
                icon: 'warning',
                title: t('devis.noClientTitle'),
                text: t('devis.noClientMessage'),
                confirmButtonText: t('common.ok', 'OK')
            });
            setModalSearchTerm(''); // Clear the search term
            setChooseProductModal(false);

            return;
        }


        // Check if product already exists in items array
        const productExists = items.some(item => item.articleId === product.id);

        if (productExists) {
            // Show warning message using Swal
            Swal.fire({
                icon: 'warning',
                title: t('purchase.productExistsTitle'),
                text: t('purchase.productExistsMessage'),
                confirmButtonText: t('common.ok')
            });
            return;
        }
        const prices = [product.prixVenteTTC1, product.prixVenteTTC2, product.prixVenteTTC3];

        const newItem = {
            articleId: product.id,
            articleNom: product.libelle,
            prices: prices,
            quantiteLivree: 1,
            prixUnitaireTTC: roundToNearestInteger(product.prixVenteTTC1),
            prixUnitaireHT: roundToNearestInteger(product.prixVenteTTC1 / (1 + (product.tauxTVAVente || 0.2))),
            unite: unites.find(u => u.code === product.unite).libelle,
            tauxTVA: product.tauxTVAVente || 0.2,
            montantHT: roundToNearestInteger(product.prixVenteTTC1 / (1 + (product.tauxTVAVente || 0.2))),
            montantTTC: roundToNearestInteger(product.prixVenteTTC1),
            prixVariable: product.prixVariable || false,
        };

        const newItems = [...items, newItem];
        setItems(newItems);
        setFieldValue('lignes', newItems);

        // Recalculate total amounts after adding a new item
        let newHtCumul = 0;
        let newTtcCumul = 0;
        let montantTotalTaxe = 0;

        newItems.forEach(item => {
            const quantity = parseFloat(item.quantiteLivree) || 0;
            const unitPrice = parseFloat(item.prixUnitaireHT) || 0;
            const tauxTVA = parseFloat(item.tauxTVA) || 0;

            newHtCumul += quantity * unitPrice;
            newTtcCumul += (quantity * unitPrice) * (1 + tauxTVA);
            montantTotalTaxe += (quantity * unitPrice) * tauxTVA;
        });

        setFieldValue('montantTotalTaxe', roundToNearestInteger(montantTotalTaxe));
        setFieldValue('montantTotalHT', roundToNearestInteger(newHtCumul));
        setFieldValue('montantTotalTTC', roundToNearestInteger(newTtcCumul));

        setModalSearchTerm('');
        setChooseProductModal(false);
    };

    const productOptions = (products || []).map((product) => ({
        value: product.id,
        label: product.libelle,
        product,
    }));

    const articleSelectStyles = {
        control: (base) => ({
            ...base,
            minHeight: 34,
            fontSize: '0.875rem',
        }),
        valueContainer: (base) => ({
            ...base,
            padding: '0 8px',
        }),
        indicatorsContainer: (base) => ({
            ...base,
            height: 34,
        }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    };

    const getSelectedArticleOption = (item) => {
        if (!item?.articleId) {
            return null;
        }

        const product = products?.find(p => p.id === item.articleId);
        return {
            value: item.articleId,
            label: product?.libelle || item.articleNom || `Article #${item.articleId}`,
            product: product || item,
        };
    };

    const handleArticleSearch = (query, meta) => {
        if (meta.action !== 'input-change') {
            return query;
        }

        setCurrentPage(1);
        setModalSearchTerm(query);
        debouncedModalSearch(query);
        return query;
    };

    const handleAddLine = () => {
        const newItem = {
            articleId: null,
            articleNom: '',
            prices: [],
            quantiteLivree: 1,
            prixUnitaireTTC: 0,
            prixUnitaireHT: 0,
            unite: '',
            tauxTVA: 0.2,
            montantHT: 0,
            montantTTC: 0,
            prixVariable: false,
        };

        recalculateAndSetItems([...items, newItem]);
    };

    const handleArticleSelect = (index, selectedOption) => {
        if (!values.clientId) {
            Swal.fire({
                icon: 'warning',
                title: t('devis.noClientTitle'),
                text: t('devis.noClientMessage'),
                confirmButtonText: t('common.ok', 'OK')
            });
            return;
        }

        if (!selectedOption?.product) {
            return;
        }

        const product = selectedOption.product;
        const productExists = items.some((item, itemIndex) => itemIndex !== index && item.articleId === product.id);
        if (productExists) {
            Swal.fire({
                icon: 'warning',
                title: t('purchase.productExistsTitle'),
                text: t('purchase.productExistsMessage'),
                confirmButtonText: t('common.ok')
            });
            return;
        }

        const prices = [product.prixVenteTTC1, product.prixVenteTTC2, product.prixVenteTTC3].filter(price => Number(price) > 0);
        const tauxTVA = product.tauxTVAVente || 0.2;
        const prixUnitaireTTC = (prices[0] || product.prixVenteTTC1 || 0);
        const uniteInfo = unites?.find(u => u.code === product.unite);
        const newItems = [...items];

        newItems[index] = {
            ...newItems[index],
            articleId: product.id,
            articleNom: product.libelle,
            prices,
            quantiteLivree: newItems[index]?.quantiteLivree || 1,
            prixUnitaireTTC,
            prixUnitaireHT: roundToNearestInteger(prixUnitaireTTC / (1 + tauxTVA)),
            unite: uniteInfo?.libelle || product.unite || '',
            tauxTVA,
            montantHT: roundToNearestInteger(prixUnitaireTTC / (1 + tauxTVA)),
            montantTTC: prixUnitaireTTC,
            prixVariable: product.prixVariable || false,
        };
        console.log("Updated item:", newItems);
        recalculateAndSetItems(newItems);
    };


    const handleArrowNavigation = (e, rowIndex, fieldName) => {
        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
            e.preventDefault(); // prevent value increment/decrement

            const direction = e.key === "ArrowUp" ? -1 : 1;
            const nextRowIndex = rowIndex + direction;

            if (nextRowIndex >= 0 && nextRowIndex < items.length) {
                const nextInput = document.querySelector(
                    `input[name="${fieldName}"][data-row="${nextRowIndex}"]`
                );
                if (nextInput) {
                    nextInput.focus();
                    nextInput.select();
                }
            }
        }
    };

    return (
        <Container fluid className="px-0 document-lines-editor">
            <div className="line-toolbar mb-2">
                <div>
                    <h6 className="mb-0">{t('facture.addLine')}</h6>
                    <span>{items.length} {t('common.lines', 'lignes')}</span>
                </div>
                <Button
                    type="button"
                    color="primary"
                    size="sm"
                    onClick={handleAddLine}
                    disabled={disabled}
                >
                    <FaPlus className="me-1" /> {t('facture.addLine', 'Ajouter ligne')}
                </Button>
            </div>

            <div className="compact-lines-table">
                <Table hover responsive className="align-middle mb-0">
                    <thead>
                        <tr>
                            <th>{t('devis.article')}</th>
                            <th>{t('devis.quantite')}</th>
                            <th>{t('devis.prixVente')}</th>
                            <th>{t('devis.prixUnitaireTTC')}</th>
                            <th>{t('devis.montantTTC')}</th>
                            <th className="text-end">{t('common.actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="text-center py-4 text-muted">
                                    {t('purchase.noItems')}
                                </td>
                            </tr>
                        ) : (
                            items.map((item, index) => (
                                <tr key={index}>
                                    <td>
                                        <Select
                                            value={getSelectedArticleOption(item)}
                                            options={productOptions}
                                            onChange={(selectedOption) => handleArticleSelect(index, selectedOption)}
                                            onInputChange={handleArticleSearch}
                                            isSearchable
                                            isClearable={false}
                                            isDisabled={disabled}
                                            isLoading={loading || isSearching}
                                            placeholder={t('product.searchPlaceholder')}
                                            styles={articleSelectStyles}
                                            menuPortalTarget={document.body}
                                            formatOptionLabel={(option) => (
                                                <div>
                                                    <div className="fw-semibold">{option.product?.libelle || option.label}</div>
                                                    <small className="text-muted">
                                                        {[option.product?.code, option.product?.unite, formatPrice(option.product?.prixVenteTTC1 || 0, false)].filter(Boolean).join(' - ')}
                                                    </small>
                                                </div>
                                            )}
                                        />
                                        {item.unite && <div className="line-sub">{item.unite}</div>}
                                        {touched.lignes?.[index]?.articleId && errors.lignes?.[index]?.articleId && (
                                            <div className="text-danger small mt-1">{errors.lignes[index].articleId}</div>
                                        )}
                                    </td>
                                    <td>
                                        <Input
                                            type="number"
                                            name="quantiteLivree"
                                            data-row={index}
                                            value={item.quantiteLivree}
                                            onChange={(event) => handleInputChange(index, event)}
                                            onKeyDown={(e) => handleArrowNavigation(e, index, "quantiteLivree")}
                                            onFocus={(e) => {
                                                e.target.select();
                                                e.target.addEventListener("wheel", preventScroll, { passive: false });
                                            }}
                                            onBlur={(e) => {
                                                e.target.removeEventListener("wheel", preventScroll);
                                            }}
                                            disabled={disabled}
                                            className="no-spinner compact-number-input"
                                        />
                                    </td>
                                    <td>
                                        {item.prices?.length > 0 ? (
                                            <Input
                                                type="select"
                                                value={item.prixUnitaireTTC}
                                                onChange={(event) => handleInputChange(index, {
                                                    target: { name: 'prixUnitaireTTC', value: event.target.value }
                                                })}
                                                disabled={disabled}
                                                className="compact-price-select"
                                            >
                                                {item.prices.map((price, priceIndex) => (
                                                    <option key={`${price}-${priceIndex}`} value={price}>
                                                        {formatPrice(price, false)}
                                                    </option>
                                                ))}
                                            </Input>
                                        ) : (
                                            <span className="line-sub">-</span>
                                        )}
                                    </td>
                                    <td>
                                        <Input
                                            type="number"
                                            name="prixUnitaireTTC"
                                            data-row={index}
                                            value={item.prixUnitaireTTC}
                                            onChange={(event) => handleInputChange(index, event)}
                                            onKeyDown={(e) => handleArrowNavigation(e, index, "prixUnitaireTTC")}
                                            onFocus={(e) => {
                                                e.target.select();
                                                e.target.addEventListener("wheel", preventScroll, { passive: false });
                                            }}
                                            onBlur={(e) => {
                                                e.target.removeEventListener("wheel", preventScroll);
                                            }}
                                            disabled={disabled}
                                            step="0.01"
                                            className="no-spinner compact-price-input"
                                        />
                                        <div className="line-sub">
                                            HT {formatPrice(item.prixUnitaireHT, false)} - TVA {(Number(item.tauxTVA || 0) * 100).toFixed(0)}%
                                        </div>
                                    </td>
                                    <td>
                                        <div className="line-total">{formatPrice(item.montantTTC, false)}</div>
                                        <div className="line-sub">HT {formatPrice(item.montantHT, false)}</div>
                                    </td>
                                    <td className="text-end">
                                        <Button color="danger" outline onClick={() => handleDeleteItem(index)} size="sm" disabled={disabled}>
                                            <FaTrash />
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
            </div>
        </Container>
    );
};
export default CommandeLine;
